export interface AcceptCardData {
  cardNumber: string
  month: string
  year: string
  cardCode: string
}

export interface AcceptAuthData {
  apiLoginID: string
  clientKey: string
}

export type AuthnetEnvironment = 'sandbox' | 'production'

export interface AuthnetConfig {
  authData: AcceptAuthData
  environment: AuthnetEnvironment
}

interface AcceptMessage {
  code: string
  text: string
}

export interface AcceptResponse {
  messages: { resultCode: string; message: AcceptMessage[] }
  opaqueData: { dataDescriptor: string; dataValue: string }
}

interface AcceptJs {
  dispatchData: (
    data: { authData: AcceptAuthData; cardData: AcceptCardData },
    callback: (response: AcceptResponse) => void,
  ) => void
}

declare global {
  interface Window {
    Accept?: AcceptJs
  }
}

const ACCEPT_JS_URLS: Record<AuthnetEnvironment, string> = {
  sandbox: 'https://jstest.authorize.net/v1/Accept.js',
  production: 'https://js.authorize.net/v1/Accept.js',
}

let acceptPromise: Promise<AcceptJs> | undefined
let acceptEnvironment: AuthnetEnvironment | undefined

export function loadAcceptJs(
  environment: AuthnetEnvironment,
): Promise<AcceptJs> {
  if (acceptPromise) {
    // Accept.js is a global script; once loaded for one environment it cannot
    // be swapped without a page reload. Fail loudly rather than tokenize
    // against the wrong endpoint.
    if (acceptEnvironment !== environment) {
      return Promise.reject(
        new Error(
          `Accept.js already loaded for "${acceptEnvironment ?? 'unknown'}", cannot switch to "${environment}"`,
        ),
      )
    }
    return acceptPromise
  }
  acceptEnvironment = environment
  acceptPromise = new Promise<AcceptJs>((resolve, reject) => {
    if (window.Accept) {
      resolve(window.Accept)
      return
    }
    const script = document.createElement('script')
    script.src = ACCEPT_JS_URLS[environment]
    script.async = true
    script.onload = () => {
      if (window.Accept) resolve(window.Accept)
      else reject(new Error('Accept.js loaded without a global Accept'))
    }
    script.onerror = () => {
      acceptPromise = undefined
      acceptEnvironment = undefined
      reject(new Error('Failed to load Accept.js'))
    }
    document.head.appendChild(script)
  })
  return acceptPromise
}

const NOT_LOADED_PATTERN = /not loaded correctly/i
const TOKENIZE_MAX_ATTEMPTS = 6
const TOKENIZE_RETRY_MS = 300

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function dispatch(
  accept: AcceptJs,
  authData: AcceptAuthData,
  cardData: AcceptCardData,
): Promise<AcceptResponse> {
  return new Promise<AcceptResponse>((resolve) => {
    accept.dispatchData({ authData, cardData }, resolve)
  })
}

function isCoreNotReady(response: AcceptResponse): boolean {
  return (
    response.messages.resultCode !== 'Ok' &&
    response.messages.message.some((message) =>
      NOT_LOADED_PATTERN.test(message.text),
    )
  )
}

export async function tokenizeWithAcceptJs(
  config: AuthnetConfig,
  cardData: AcceptCardData,
): Promise<AcceptResponse> {
  const accept = await loadAcceptJs(config.environment)
  let response = await dispatch(accept, config.authData, cardData)
  for (
    let attempt = 1;
    attempt < TOKENIZE_MAX_ATTEMPTS && isCoreNotReady(response);
    attempt += 1
  ) {
    await delay(TOKENIZE_RETRY_MS)
    response = await dispatch(accept, config.authData, cardData)
  }
  return response
}
